import {
  FormControl,
  FormHelperText,
  ListSubheader,
  MenuItem,
  Select,
  SelectChangeEvent,
  IconButton,
  styled,
  SxProps,
  Theme,
  Autocomplete,
  TextField,
  Paper,
  Box,
} from "@mui/material";
import React, { useState } from "react";
import { useTheme } from "@mui/material/styles";
import { COLORS } from "../assets/theme";
import CustomCheckbox from "./CustomCheckbox";
import CustomTextField from "./CustomTextField";
import ClearIcon from "@mui/icons-material/Clear";
import CustomLabel from "./CustomLabel";

type DropdownOption = { label: string; value: string | number };

export type ICustomSelectProps = {
  label?: string;
  labelComponent?: React.ReactNode;
  value: string | number | boolean | (string | number)[];
  options: DropdownOption[];
  onChange: (value: string | number | (string | number)[]) => void;
  onBlur?: (value: string | number | (string | number)[]) => void;
  placeholder?: string;
  size?: "small" | "medium" | "large";
  fullWidth?: boolean;
  error?: boolean;
  helperText?: string;
  bgColor?: string;
  multiSelect?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  autoFocus?: boolean;
  highlightLabelStar?: boolean;
  clearable?: boolean;
  labelFontWeight?: number | string;
  labelFontSize?: string | number;
  sx?: SxProps<Theme>;
  xs?: number;
  sm?: number;
  md?: number;
  minWidth?: number;
  lg?: number;
  renderPlaceholder?: boolean;
  selectAll?: boolean;
  searchable?: boolean;
  textable?: boolean;
  searchValue?: string;
  onSearchValueChange?: (value: string) => void;
  searchPlaceholder?: string;
  isDeleted?: boolean;
  onToggleDeleted?: () => void;
  hidden?: boolean;
};

const StyledSelect = styled(Select)<{ $bgColor?: string; $disabled?: boolean }>(
  ({ $bgColor, $disabled }) => ({
    backgroundColor: $disabled ? "#f0f0f0" : $bgColor || "transparent",
    borderRadius: "12px",
    pointerEvents: $disabled ? "none" : "auto",
    opacity: $disabled ? 0.6 : 1,
    cursor: $disabled ? "not-allowed" : "pointer",
    "& .MuiSelect-select": {
      padding: "8px 12px",
      fontSize: "1rem",
      borderRadius: "12px",
      "&.MuiSelect-select": {
        color: "inherit",
      },
    },
    "& .placeholder": {
      color: "#868585",
      borderRadius: "0px",
      fontSize: "0.85rem",
    },
    "& .MuiListSubheader-root": {
      position: "sticky",
      top: 0,
      zIndex: 1,
      backgroundColor: "white",
    },
    "&.Mui-disabled": {
      backgroundColor: "#f0f0f0",
      cursor: "not-allowed",
    },
  })
);

const StyledTextField = styled(TextField)<{
  $bgColor?: string;
  $disabled?: boolean;
}>(({ $bgColor, $disabled, theme }) => ({
  backgroundColor: $disabled ? "#f0f0f0" : $bgColor || "transparent",
  borderRadius: "12px",
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    "& input": {
      padding: "8px 12px",
      color: $disabled ? "#c0c0c0" : "#000000",
      fontSize: "1rem",
    },
    "& fieldset": {
      borderColor: theme.palette.mode === "light" ? "#e0e0e0" : "#2c2c2c",
    },
    "&:hover fieldset": {
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.primary.main,
    },
  },
  "& .placeholder": {
    color: "#868585",
    fontSize: "0.85rem",
  },
}));

const StyledFormControl = styled(FormControl)<{
  $size: "small" | "medium" | "large";
  $fullWidth?: boolean;
  $minWidth?: string | number;
}>(({ $size, $fullWidth, $minWidth, theme }) => ({
  borderRadius: "12px",
  width: $fullWidth
    ? "100%"
    : $size === "small"
    ? "250px"
    : $size === "medium"
    ? "368px"
    : "463px",
  minWidth: $minWidth || "auto",
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    minWidth: 0,
  },
}));

const CustomSelect: React.FC<ICustomSelectProps> = ({
  label,
  value,
  options,
  onChange,
  onBlur,
  placeholder = "Select an option",
  size = "small",
  fullWidth = false,
  error = false,
  helperText = "",
  bgColor = "transparent",
  multiSelect = false,
  disabled = false,
  highlightLabelStar = false,
  labelFontWeight = "500",
  labelFontSize = "0.85rem",
  sx = {},
  labelComponent,
  minWidth,
  renderPlaceholder = false,
  selectAll = false,
  searchable = false,
  textable = false,
  searchValue: externalSearchValue,
  onSearchValueChange: externalOnSearchValueChange,
  searchPlaceholder = "Search...",
  isDeleted = false,
  onToggleDeleted,
  clearable = false,
  hidden = false,
}) => {
  const theme = useTheme();
  const SELECT_ALL_VALUE = "__select_all__";
  const validOptions = React.useMemo(
    () =>
      options.filter(
        (option) => option.value !== undefined && option.value !== null
      ),
    [options]
  );
  const [internalSearchValue, setInternalSearchValue] = useState("");
  const [selectOpen, setSelectOpen] = useState(false);
  const selectRef = React.useRef<HTMLDivElement>(null);
  const [menuWidth, setMenuWidth] = useState<number | undefined>(undefined);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const labelId = `custom-select-label-${React.useId()}`;

  React.useEffect(() => {
    if (selectRef.current) {
      setMenuWidth(selectRef.current.offsetWidth);
    }
    const handleResize = () => {
      if (selectRef.current) {
        setMenuWidth(selectRef.current.offsetWidth);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [selectOpen]);

  const searchValue =
    externalSearchValue !== undefined
      ? externalSearchValue
      : internalSearchValue;

  const sanitizedArrayValue = Array.isArray(value)
    ? value.filter((v) => v !== undefined && v !== null)
    : [];

  const allSelected =
    multiSelect &&
    Array.isArray(sanitizedArrayValue) &&
    sanitizedArrayValue.length === validOptions.length;

  const handleSelectAllToggle = (
    event?:
      | React.ChangeEvent<HTMLInputElement>
      | React.MouseEvent
      | React.SyntheticEvent
  ) => {
    event?.stopPropagation();
    const newValue = allSelected
      ? []
      : validOptions.map((option) => option.value);
    onChange(newValue);
  };

  const toggleValueInArray = (
    currentValues: (string | number)[],
    valueToToggle: string | number
  ): (string | number)[] => {
    if (valueToToggle === undefined || valueToToggle === null) {
      return currentValues;
    }
    return currentValues.includes(valueToToggle)
      ? currentValues.filter((v) => v !== valueToToggle)
      : [...currentValues, valueToToggle];
  };

  const handleChange = (e: SelectChangeEvent<unknown>) => {
    onChange(e.target.value as string | number | (string | number)[]);
  };

  const computedValue = multiSelect
    ? sanitizedArrayValue
    : value;

  const filteredOptions = validOptions.filter((option) =>
    option.label.toLowerCase().includes(searchValue.toLowerCase())
  );

  const finalOptions = searchValue ? filteredOptions : validOptions;

  const renderValue = (selected: unknown) => {
    const nothingSelected =
      selected === null ||
      selected === undefined ||
      selected === "" ||
      (Array.isArray(selected) && selected.length === 0);

    if (nothingSelected) {
      return (
        <span className="placeholder">
          {label || placeholder || "Select..."}
        </span>
      );
    }
    if (Array.isArray(selected)) {
      const selectedValues = selected
        .filter((val) => val !== SELECT_ALL_VALUE)
        .filter((val) => val !== undefined && val !== null);
      if (selectedValues.length === validOptions.length) {
        return "All Selected";
      }
      return selectedValues
        .map(
          (val) =>
            validOptions.find(
              (option) =>
                option.value !== undefined &&
                option.value !== null &&
                option.value.toString() === val.toString()
            )
              ?.label
        )
        .join(", ");
    }
    return (
      validOptions.find(
        (option) =>
          option.value !== undefined &&
          option.value !== null &&
          option.value.toString() === selected.toString()
      )
        ?.label || placeholder
    );
  };

  const handleOptionSelect = (
    event: React.SyntheticEvent,
    value: DropdownOption | DropdownOption[] | null
  ) => {
    if (value) {
      if (Array.isArray(value)) {
        onChange(value.map((option) => option.value));
      } else {
        onChange(value.value);
      }
    } else {
      onChange(multiSelect ? [] : "");
    }
  };

  const renderOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: DropdownOption
  ) => {
    if (multiSelect && selectAll) {
      return (
        <li {...props}>
          <Box sx={{ width: "50px" }}>
            <CustomCheckbox
              checked={
                Array.isArray(computedValue) &&
                computedValue.includes(option.value)
              }
              label={option.label}
              onChange={(e) => {
                e.stopPropagation();
                if (Array.isArray(computedValue)) {
                  const newValue = toggleValueInArray(
                    computedValue,
                    option.value
                  );
                  onChange(newValue);
                }
              }}
            />
          </Box>
        </li>
      );
    }
    return <li {...props}>{option.label}</li>;
  };

  const selectedOptions = multiSelect
    ? validOptions.filter(
        (option) =>
          Array.isArray(computedValue) &&
          computedValue.includes(option.value)
      )
    : validOptions.find((option) => option.value === value) || null;

  if (hidden) {
    return null;
  }

  return (
    <StyledFormControl
      fullWidth={fullWidth}
      error={error}
      $size={size}
      $fullWidth={fullWidth}
      $minWidth={minWidth}
      disabled={disabled}
      sx={sx}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {textable ? (
          <Autocomplete
            multiple={multiSelect}
            options={validOptions}
            getOptionLabel={(option) => {
              if (typeof option === "string") return "";
              return option.label;
            }}
            renderInput={(params) => (
              <StyledTextField
                {...params}
                label={label}
                placeholder={
                  (Array.isArray(value) && value.length > 0) ||
                  (!Array.isArray(value) && value)
                    ? ""
                    : placeholder
                }
                error={error}
                $bgColor={bgColor}
                $disabled={disabled}
                inputProps={{
                  ...params.inputProps,
                  autoComplete: "off",
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px 0px 0px 12px",
                    height: "40px",
                    [theme.breakpoints.down("sm")]: {
                      borderRadius: "12px",
                      height: "36px",
                    },
                  },
                  width: { xs: "100%", sm: undefined },
                }}
              />
            )}
            value={selectedOptions}
            onChange={handleOptionSelect}
            disableCloseOnSelect={multiSelect}
            onInputChange={(_, newInputValue) => {
              if (externalOnSearchValueChange) {
                externalOnSearchValueChange(newInputValue);
              } else {
                setInternalSearchValue(newInputValue);
              }
            }}
            disableClearable={!clearable}
            clearOnEscape={clearable}
            renderOption={renderOption}
            filterOptions={(x) => x}
            PaperComponent={(props) => (
              <Paper
                {...props}
                elevation={3}
                sx={{
                  marginTop: "4px",
                  maxHeight: "300px",
                  overflow: "auto",
                  width: { xs: "100%", sm: undefined },
                  left: 0,
                  right: 0,
                }}
              />
            )}
            disabled={disabled}
            sx={{
              ...sx,
              "& .MuiAutocomplete-tag": {
                backgroundColor: COLORS.activeBackground,
                color: "white",
                borderRadius: "8px",
                margin: "2px",

                padding: "2px 6px",
              },
            }}
          />
        ) : (
          <StyledSelect
            value={computedValue}
            onChange={handleChange}
            multiple={multiSelect}
            $bgColor={bgColor}
            $disabled={disabled}
            renderValue={renderValue}
            displayEmpty={true}
            size={size === "large" ? "medium" : size}
            disabled={disabled}
            open={selectOpen && !disabled}
            onOpen={() => {
              if (!disabled) {
                setSelectOpen(true);
              }
            }}
            onClose={() => setSelectOpen(false)}
            sx={{
              ...sx,
              width: { xs: "100%", sm: undefined },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  width: menuWidth ? `${menuWidth}px` : "100%",
                  minWidth: "unset",
                  maxWidth: "unset",
                },
              },
              sx: {
                "& .MuiPaper-root": {
                  maxHeight: "300px",
                  overflow: "auto",
                  left: 0,
                  right: 0,
                },
              },
            }}
            ref={selectRef}
          >
            {onToggleDeleted && (
              <Box
                component={MenuItem}
                role="menuitem"
                sx={{
                  px: 2,
                  py: 1,
                  cursor: "pointer",
                  listStyle: "none",
                }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onToggleDeleted();
                  setTimeout(() => setSelectOpen(true), 0);
                }}
              >
                {isDeleted ? "Show Not Deleted" : "Show  Deleted"}
              </Box>
            )}
            {searchable && (
              <ListSubheader
                disableSticky
                sx={{
                  backgroundColor: "white",
                  borderBottom: "1px solid #eaeaea",
                  px: 1,
                  pt: 1,
                  pb: 0.5,
                  lineHeight: "normal",
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setTimeout(() => {
                    searchInputRef.current?.focus();
                  }, 0);
                }}
              >
                <CustomTextField
                  placeholder={searchPlaceholder}
                  autoFocus={true}
                  value={searchValue || ""}
                  onChange={(e) => {
                    if (externalOnSearchValueChange) {
                      externalOnSearchValueChange(e.target.value);
                    } else {
                      setInternalSearchValue(e.target.value);
                    }
                    setTimeout(() => {
                      searchInputRef.current?.focus();
                    }, 0);
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                  }}
                  inputRef={searchInputRef}
                  fullWidth
                  sx={{
                    "& .MuiInputBase-input": {
                      p: "8px",
                      fontSize: "0.875rem",
                      backgroundColor: "transparent",
                      border: "none",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                      borderRadius: "12px 0px 0px 12px",
                    },
                  }}
                />
              </ListSubheader>
            )}

            {multiSelect && selectAll && (
              <Box
                sx={{
                  color: "#868585",
                  display: "flex",
                  alignItems: "center",
                  cursor: "default",
                  width: "100%",
                  px: 2,
                  borderBottom: "1px solid #eaeaea",
                  pb: 1,
                  mb: 0.5,
                }}
              >
                <CustomCheckbox
                  checked={allSelected}
                  onChange={handleSelectAllToggle}
                  ariaLabel="select all"
                />
                <Box
                  component="span"
                  sx={{ ml: 1, flexGrow: 1 }}
                >
                  {allSelected ? "Deselect All" : "Select All"}
                </Box>
              </Box>
            )}
            {renderPlaceholder && (
              <MenuItem value="" sx={{ color: "#868585" }}>
                <em>{placeholder}</em>
              </MenuItem>
            )}
            {finalOptions.map((option, index) => (
              <MenuItem
                key={index}
                value={option.value}
                disabled={disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  if (multiSelect) {
                    const current = Array.isArray(computedValue)
                      ? (computedValue as (string | number)[])
                      : [];
                    const newValue = toggleValueInArray(current, option.value);
                    onChange(newValue);
                  } else {
                    onChange(option.value);
                    setSelectOpen(false);
                  }
                }}
              >
                {multiSelect ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <CustomCheckbox
                      checked={
                        Array.isArray(computedValue) &&
                        computedValue.includes(option.value)
                      }
                      onChange={(e) => {
                        e.stopPropagation();
                        if (Array.isArray(computedValue)) {
                          const newValue = toggleValueInArray(
                            computedValue,
                            option.value
                          );
                          onChange(newValue);
                        }
                      }}
                      ariaLabel={`Select ${option.label}`}
                    />
                    <Box component="span" sx={{ ml: 1, flexGrow: 1 }}>
                      {option.label}
                    </Box>
                  </Box>
                ) : (
                  option.label
                )}
              </MenuItem>
            ))}
          </StyledSelect>
        )}

        {labelComponent}

        {!textable &&
          clearable &&
          !disabled &&
          (Array.isArray(computedValue)
            ? computedValue.length > 0
            : !!computedValue) && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                if (multiSelect) {
                  onChange([]);
                } else {
                  onChange("");
                }
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              aria-label="clear selection"
              sx={{
                position: "absolute",
                right: { xs: 32, sm: 40 },
                top: "50%",
                transform: "translateY(-50%)",
                bgcolor: "rgba(0,0,0,0.04)",
                borderRadius: 1,
                p: 0.5,
                "&:hover": { bgcolor: "rgba(0,0,0,0.08)" },
                color: "text.secondary",
                zIndex: 1,
                minWidth: "24px",
                height: "24px",
              }}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          )}
      </Box>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </StyledFormControl>
  );
};

export default CustomSelect;
